import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

interface BlankSongTitleProps {
  title: string;
  roundStartTime: number;
  revealFirstLettersAfter?: number; // in milliseconds, default 30 seconds
}

const isAlphaNum = (char: string) => /[a-zA-Z0-9]/.test(char);

const BlankSongTitle: React.FC<BlankSongTitleProps> = ({ 
  title, 
  roundStartTime, 
  revealFirstLettersAfter = 30000 
}) => {
  const [displayTitle, setDisplayTitle] = useState<string[]>([]);
  const [hintRevealed, setHintRevealed] = useState(false);
  // Lock the title for the duration of a round so the blank "shape"
  // doesn't change if the parent updates `title` mid-round.
  const [lockedTitle, setLockedTitle] = useState(title);
  const lastLockedRoundStartTimeRef = useRef<number | null>(null);

  const makeBlankedTitle = (songTitle: string) =>
    songTitle.split('').map((char) => {
      if (char === ' ') return ' ';
      if (!isAlphaNum(char)) return char; // Keep punctuation
      return '_';
    });

  const makeFirstLettersRevealedTitle = (songTitle: string) => {
    const titleChars = songTitle.split('');
    const newDisplayTitle: string[] = new Array(titleChars.length);

    let isFirstLetterOfWord = true;

    for (let i = 0; i < titleChars.length; i++) {
      const char = titleChars[i];

      if (char === ' ') {
        newDisplayTitle[i] = ' ';
        isFirstLetterOfWord = true;
      } else if (!isAlphaNum(char)) {
        newDisplayTitle[i] = char;
      } else if (isFirstLetterOfWord) {
        newDisplayTitle[i] = char;
        isFirstLetterOfWord = false;
      } else {
        newDisplayTitle[i] = '_';
      }
    }

    return newDisplayTitle;
  };

  useEffect(() => {
    // New round => lock in whatever title was provided at round start.
    // We include `title` in deps, but only re-lock when the round actually changes.
    if (lastLockedRoundStartTimeRef.current !== roundStartTime) {
      lastLockedRoundStartTimeRef.current = roundStartTime;
      setLockedTitle(title);
    }
  }, [roundStartTime, title]);

  useEffect(() => {
    const songTitle = lockedTitle;

    // Create initial blanked title
    setDisplayTitle(makeBlankedTitle(songTitle));
    setHintRevealed(false);

    // Reveal based on elapsed round time (not component mount time)
    const elapsed = roundStartTime ? Date.now() - roundStartTime : 0;
    const remaining = revealFirstLettersAfter - elapsed;

    if (remaining <= 0) {
      setDisplayTitle(makeFirstLettersRevealedTitle(songTitle));
      setHintRevealed(true);
      return;
    }

    const timer = setTimeout(() => {
      setDisplayTitle(makeFirstLettersRevealedTitle(songTitle));
      setHintRevealed(true);
    }, remaining);

    return () => clearTimeout(timer);
  }, [lockedTitle, roundStartTime, revealFirstLettersAfter]);

  // Group characters into words so wrapping happens at word boundaries.
  const words: { start: number; chars: string[] }[] = [];
  let current: { start: number; chars: string[] } | null = null;
  displayTitle.forEach((char, index) => {
    if (char === ' ') {
      current = null;
      return;
    }
    if (!current) {
      current = { start: index, chars: [] };
      words.push(current);
    }
    current.chars.push(char);
  });

  const letterCount = lockedTitle.split('').filter(isAlphaNum).length;

  return (
    <div className="blank-title">
      <div className="blank-title-meta">
        <span className="blank-title-label">Guess the title</span>
        <span className="blank-title-hint">
          {letterCount} letters{hintRevealed ? ' · first letters revealed' : ''}
        </span>
      </div>
      <div className="blank-word-row" aria-label={`Song title with ${letterCount} letters`}>
        {words.map((word) => (
          <span className="blank-word" key={word.start}>
            {word.chars.map((char, i) => {
              const index = word.start + i;
              const isBlank = char === '_';
              const kind = isBlank ? 'blank' : isAlphaNum(char) ? 'letter' : 'punct';
              return (
                <span key={index} className={`blank-char blank-char--${kind}`}>
                  {!isBlank && (
                    <motion.span
                      key={char}
                      className="blank-char-glyph"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35, delay: index * 0.02, ease: [0.2, 0.8, 0.2, 1] }}
                    >
                      {char}
                    </motion.span>
                  )}
                </span>
              );
            })}
          </span>
        ))}
      </div>
    </div>
  );
};

export default BlankSongTitle; 
